import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { MyFavoritePlanFormProvider, useMyFavoritePlanForm } from '../context/MyFavoritePlanFormContext';
import { FAV_PLAN_SUMMARY_SLUG, MY_FAVORITE_PLAN_STEPS, buildFavPlanPath } from '../constants/myFavoritePlanForm';
import FavoritePlanFooterComponent from '../pages/MyFavoritePlanForm/components/FavoritePlanFooterComponent';
import FavoritePlanSidebarSteps from '../pages/MyFavoritePlanForm/components/FavoritePlanSidebarSteps';
import BackToHomeButton from '../pages/MyFavoritePlanForm/components/BackToHomeButton';
import { useContext, useEffect } from 'react';
import { TextsContext } from '../context/textsContext';
import UnsavedChangesModal from '../components/UnsavedChangesModal';
import FavoritePlanFormSkeleton from '../pages/MyFavoritePlanForm/components/FavoritePlanFormSkeleton';
import FavoritePlanLoadError from '../pages/MyFavoritePlanForm/components/FavoritePlanLoadError';
import StaleFavoritePlanWarningModal from '../components/staleFavoritePlan/StaleFavoritePlanWarningModal';
import style from '../scss/formLayout.module.scss';

/**
 * Contenido del layout. Va dentro del provider porque necesita el estado de carga
 * del plan en modo edición: hasta que el plan llega, los pasos se renderizarían
 * vacíos (y el usuario vería un formulario en blanco que se rellena de golpe).
 */
const MyFavoritePlanFormContent = () => {
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const { texts } = useContext(TextsContext);
    const {
        isLoadingFavoritePlanForm,
        isFavoritePlanError,
        staleBenefitWarning,
        dismissStaleBenefitWarning,
        pendingPath,
        confirmLeave,
        cancelLeave,
        isFemale,
        favoritePlanId,
    } = useMyFavoritePlanForm();

    // endsWith y no igualdad: en modo edición la ruta lleva el id (/fav-plan/:id/summary).
    const isSummaryStep = pathname.endsWith(`/${FAV_PLAN_SUMMARY_SLUG}`);
    const hideFooter = isSummaryStep || isLoadingFavoritePlanForm || isFavoritePlanError;

    // Defensa para navegación directa (URL o sidebar): el rider de maternidad no
    // es navegable si el usuario no es elegible (ver getApplicableFavPlanSteps),
    // así que si de todos modos aterriza acá, se lo saca al siguiente paso real -
    // mismo rol que cumple el redirect de QuoterFormLayout cuando currentPathIndex
    // da undefined para su propio step de maternidad.
    const isOnMaternityStep = pathname.endsWith('/riders-maternity');
    const eligibleForMaternity = isFemale();

    useEffect(() => {
        if (!isOnMaternityStep || isLoadingFavoritePlanForm || eligibleForMaternity) return;

        const maternityIndex = MY_FAVORITE_PLAN_STEPS.findIndex(step => step.slug === 'riders-maternity');
        const nextStep = MY_FAVORITE_PLAN_STEPS[maternityIndex + 1];
        navigate(buildFavPlanPath(nextStep.slug, favoritePlanId), { replace: true });
    }, [isOnMaternityStep, isLoadingFavoritePlanForm, eligibleForMaternity, favoritePlanId, navigate]);

    const renderContent = () => {
        if (isFavoritePlanError) return <FavoritePlanLoadError />;
        if (isLoadingFavoritePlanForm) return <FavoritePlanFormSkeleton />;
        return <Outlet />;
    };

    return (
        <>
            <div className={style.wrapper}>


                <aside className={style.sidebar}>
                    <FavoritePlanSidebarSteps />
                    <BackToHomeButton />
                </aside>

                <main className={style.content}>
                    <div className={style.contentScroll}>
                        {renderContent()}
                    </div>
                    {!hideFooter && (
                        <div className={style.footer}>
                            <FavoritePlanFooterComponent />
                        </div>
                    )}
                </main>

            </div>

            <UnsavedChangesModal show={!!pendingPath} onConfirm={confirmLeave} onCancel={cancelLeave} />

            <StaleFavoritePlanWarningModal
                show={staleBenefitWarning}
                onConfirm={dismissStaleBenefitWarning}
                confirmText={texts?.fav_plan_stale_benefits_acknowledge ?? 'OK'}
            />
        </>
    );
};

const MyFavoritePlanFormLayout = () => (
    <MyFavoritePlanFormProvider>
        <MyFavoritePlanFormContent />
    </MyFavoritePlanFormProvider>
);

export default MyFavoritePlanFormLayout;
